<template>
  <div id="tour-settings-page" class="flex flex-1 space-x-2 ">
    <DashboardHeaderTab :title="'Settings'" :tabs="tabs" :activeTab="activeTab" :setActiveTab="setActiveTab"
      :toggleSidebar="toggleSidebar" />

  </div>
</template>

<script setup lang="ts">
import DashboardHeaderTab from '@/components/DashBoard/common/DashboardHeaderTab.vue';
import { useTabs } from '@/composables/useTabs';
import UpdateSchool from '@/components/DashBoard/Settings/UpdateSchool.vue';
import AcademicCalendar from '@/components/DashBoard/Settings/AcademicCalendar/AcademicCalendar.vue';
import Section from '@/components/DashBoard/Settings/Section/Section.vue';
import Payment from '@/components/DashBoard/Settings/Payment/Payment.vue';
import Tertiary from '@/components/DashBoard/Settings/Tertiary/Tertiary.vue';
import Campus from '@/components/DashBoard/Settings/Tertiary/Campus/Campus.vue';
import TicketCategories from '@/components/DashBoard/Settings/TicketCategories/TicketCategories.vue';
import PdfResultConfig from '@/components/DashBoard/Settings/PdfResultConfig.vue';
import MatricNoConfig from '@/components/DashBoard/Settings/MatricNoConfig.vue';
import IdCardConfig from '@/components/DashBoard/Settings/IdCardConfig.vue';
import AiAssistantConfig from '@/components/DashBoard/Settings/AiAssistantConfig.vue';
import CustomFieldsSettings from '@/components/DashBoard/Settings/CustomFields/CustomFieldsSettings.vue';
import { useUserStore } from '@/store/userStore';
import { AppPermissions, can } from '@/utils/permissions';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';

const { isTertiary } = useSchoolFeatures();
const userStore = useUserStore();

const toggleSidebar = inject<() => void>('toggleSidebar', () => { });

// helper label
const termLabel = computed(() =>
  isTertiary.value ? 'Semester Calendar' : 'Academic Calendar',
);

// "Classroom" for primary/secondary, "Level" for tertiary — same Section
// component, just relabeled. Tertiary schools were missing this tab entirely,
// so admins had no way to manage their levels from settings.
const classroomTabName = computed(() => (isTertiary.value ? 'Level' : 'Classroom'));

const tabs = computed(() => {
  const baseTabs = [
    { name: 'Update School', component: UpdateSchool, query: 'update' },
    { name: termLabel.value, component: AcademicCalendar, query: 'calendar' },
    { name: classroomTabName.value, component: Section, query: 'section' },
    { name: 'Payment', component: Payment, query: 'payment' },
    { name: 'Ticket Categories', component: TicketCategories, query: 'ticket_categories' },
    { name: 'Result PDF', component: PdfResultConfig, query: 'pdf_config' },
    { name: 'Matric No.', component: MatricNoConfig, query: 'matric_no' },
    { name: 'ID Card', component: IdCardConfig, query: 'id_card' },
    { name: 'AI Assistant', component: AiAssistantConfig, query: 'ai_assistant' },
  ];

  if (can(userStore.currentUser, AppPermissions.CustomFields.Actions.View, AppPermissions.CustomFields.Resource)) {
    baseTabs.push({ name: 'Custom Fields', component: CustomFieldsSettings, query: 'custom_fields' });
  }

  if (isTertiary.value) {
    baseTabs.push({ name: 'Tertiary', component: Tertiary, query: 'tertiary' });
    baseTabs.push({ name: 'Campus', component: Campus, query: 'campus' });
  }

  return baseTabs;
});
// Use the composable for tab management
const { activeTab, setActiveTab } = useTabs(tabs);

</script>

<style scoped></style>
